import { WindowManager } from '@kurtosys/window-manager';

import { EventDetail } from './models/EventDetail.js';
import { EventListener } from './models/EventListener.js';
import { EventSource } from './models/EventSource.js';
import { EventSubscription } from './models/EventSubscription.js';
import { EventTypes } from './models/EventTypes.js';
import { SourceEventManager } from './SourceEventManager.js';

export class EventManager {
	static key = 'EventManager';
	sources: Map<string, SourceEventManager> = new Map();

	private getSource(sourceKey: string) {
		return this.sources.get(sourceKey);
	}

	public addSource(sourceKey: string) {
		const sourceManager = new SourceEventManager(sourceKey);
		if (!this.sources.has(sourceManager.key)) {
			this.sources.set(sourceManager.key, sourceManager);
		}
	}

	public subscribe<TType extends EventTypes, TDetail extends EventDetail>(sourceKey: string, subscription: EventSubscription<TType, TDetail>): void {
		let sourceManager = this.getSource(sourceKey);
		// create if the publisher hasn't created it yet, it is safe to assume the publish will simply re-use this source when it has initialised.
		if (!sourceManager) {
			this.addSource(sourceKey);
			sourceManager = this.getSource(sourceKey);
		}
		sourceManager.subscribe(subscription);
	}

	public unsubscribe<TType extends EventTypes, TDetail extends EventDetail>(sourceKey: string, type: TType, listener: EventListener): void {
		const sourceManager = this.getSource(sourceKey);
		// if the source doesn't exist, it is not needed to remove the listener as it doesn't exist.
		if (sourceManager) {
			sourceManager.unsubscribe<TType, TDetail>(type, listener);
		}
	}

	public publish<TType extends EventTypes, TDetail extends EventDetail, TEventSource extends EventSource>(sourceKey: string, type: TType, detail: TDetail, source: TEventSource) {
		let sourceManager = this.getSource(sourceKey);
		// if the publisher hasn't already created the source, create it now.
		if (!sourceManager) {
			this.addSource(sourceKey);
			sourceManager = this.getSource(sourceKey);
		}
		sourceManager.publish(type, detail, source);
	}

	public static init() {
		if (!WindowManager.has(EventManager.key)) {
			WindowManager.set(EventManager.key, new EventManager());
		}
	}

	public static destroy() {
		const manager: EventManager = WindowManager.get(EventManager.key);
		if (manager) {
			manager.sources.clear();
			WindowManager.set(EventManager.key, undefined);
		}
	}

	public static get(): EventManager | undefined {
		if (!WindowManager.has(EventManager.key)) {
			console.error(`${EventManager.key} instance not found!`)
			return;
		}
		return WindowManager.get(EventManager.key);
	}
}